import { Injectable } from '@angular/core';
import Highcharts from 'highcharts';
import { Chart, Point, PointerEventObject } from 'highcharts';

@Injectable({
  providedIn: 'root',
})
export class ChartService {
  private pointIntervalThreshold: number = 7.5 * 60 * 1000;
  private granularityMs: number = 15 * 60 * 1000;
  private rafId: number | null = null;

  setPointIntervalThreshold(granularityMinutes: number): void {
    this.granularityMs = granularityMinutes * 60 * 1000;
    this.pointIntervalThreshold = this.granularityMs / 2;
  }

  private roundToGranularity(timestamp: number): number {
    return Math.round(timestamp / this.granularityMs) * this.granularityMs;
  }

  private findClosestPoint(points: Point[], targetX: number): { point: Point; distance: number } | null {
    if (!points || points.length === 0) {
      return null;
    }

    let left = 0;
    let right = points.length - 1;
    let closestPoint: Point = points[0];
    let minDistance = Math.abs(points[0].x - targetX);

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const point = points[mid];
      const distance = Math.abs(point.x - targetX);

      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = point;
      }

      if (point.x < targetX) {
        left = mid + 1;
      } else if (point.x > targetX) {
        right = mid - 1;
      } else {
        return { point, distance: 0 };
      }
    }

    const index = points.indexOf(closestPoint);
    if (index > 0) {
      const prevDistance = Math.abs(points[index - 1].x - targetX);
      if (prevDistance < minDistance) {
        minDistance = prevDistance;
        closestPoint = points[index - 1];
      }
    }
    if (index < points.length - 1) {
      const nextDistance = Math.abs(points[index + 1].x - targetX);
      if (nextDistance < minDistance) {
        minDistance = nextDistance;
        closestPoint = points[index + 1];
      }
    }

    return { point: closestPoint, distance: minDistance };
  }

  synchronizeCharts(
    charts: Chart[],
    syncHandler: (
      e: PointerEvent,
      sourceChart: Chart,
      allCharts: Chart[],
    ) => void,
  ): void {
    charts.forEach(chartInstance => {
      if (!chartInstance || !chartInstance.container) {
        return;
      }

      ['mousemove', 'touchmove', 'touchstart'].forEach(eventType => {
        Highcharts.addEvent(
          chartInstance.container,
          eventType,
          (e: PointerEvent) => {
            syncHandler(e, chartInstance, charts);
          },
        );
      });

      Highcharts.addEvent(chartInstance.container, 'mouseleave', () => {
        this.hideTooltipsAndCrosshairs(charts);
      });
      Highcharts.addEvent(chartInstance, 'selection', () => {
        this.hideTooltipsAndCrosshairs(charts);
      });

      Highcharts.addEvent(chartInstance, 'afterSetExtremes', () => {
        this.hideTooltipsAndCrosshairs(charts);
      });
    });
  }

  hideTooltipsAndCrosshairs(charts: Chart[]): void {
    charts.forEach(otherChart => {
      if (
        otherChart &&
        otherChart.tooltip &&
        otherChart.xAxis &&
        otherChart.xAxis[0]
      ) {
        otherChart.tooltip.hide();
        otherChart.xAxis[0].hideCrosshair();
        otherChart.series.forEach(s => {
          if (s.points) {
            s.points.forEach(p => p.setState && p.setState(''));
          }
        });
      }
    });
  }

  handleSyncEvent(
    e: PointerEvent,
    sourceChart: Chart,
    allCharts: Chart[],
  ): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }

    this.rafId = requestAnimationFrame(() => {
      this.performSync(e, sourceChart, allCharts);
      this.rafId = null;
    });
  }

  private performSync(
    e: PointerEvent,
    sourceChart: Chart,
    allCharts: Chart[],
  ): void {
    const eventInSource = sourceChart.pointer.normalize(
      e,
    ) as PointerEventObject;
    const xValue = sourceChart.xAxis[0].toValue(eventInSource.chartX);
    const roundedXValue = this.roundToGranularity(xValue);

    allCharts.forEach(targetChart => {
      if (!targetChart || !targetChart.series || targetChart === sourceChart) {
        return;
      }

      targetChart.series.forEach(s => {
        if (s.points) {
          s.points.forEach(p => p.setState && p.setState(''));
        }
      });

      const pointsInTarget: Point[] = [];

      targetChart.series.forEach(series => {
        if (
          series.visible &&
          (series.options as any).enableMouseTracking !== false &&
          series.points?.length
        ) {
          const result = this.findClosestPoint(series.points, xValue);
          
          if (!result) {
            return;
          }

          const { point, distance } = result;
          const pointRounded = this.roundToGranularity(point.x);
          const isExactMatch = pointRounded === roundedXValue;
          
          if (isExactMatch || distance < this.pointIntervalThreshold) {
            pointsInTarget.push(point);
          }
        }
      });

      this.updateTargetChartWithPoints(
        targetChart,
        pointsInTarget,
        eventInSource,
      );
    });
  }

  private updateTargetChartWithPoints(
    targetChart: Chart,
    pointsInTarget: Point[],
    eventInSource: PointerEventObject,
  ): void {
    targetChart.series.forEach(s => {
      if (s.points) {
        s.points.forEach(p => p.setState && p.setState(''));
      }
    });

    if (pointsInTarget.length > 0) {
      const referencePoint = pointsInTarget[0];
      const chartXInTarget = targetChart.xAxis[0].toPixels(
        referencePoint.x,
        true,
      );
      const simulatedEvent = {
        chartX: chartXInTarget,
        chartY: eventInSource.chartY,
      };

      if (targetChart.tooltip) {
        targetChart.tooltip.refresh(pointsInTarget);
      }

      if (targetChart.xAxis && targetChart.xAxis[0]) {
        targetChart.xAxis[0].drawCrosshair(
          simulatedEvent as PointerEventObject,
          referencePoint,
        );
      }

      pointsInTarget.forEach(p => p.setState && p.setState('hover'));
    } else {
      if (targetChart.tooltip) targetChart.tooltip.hide();
      if (targetChart.xAxis && targetChart.xAxis[0])
        targetChart.xAxis[0].hideCrosshair();
    }
  }

  adjustDataGranularity(
    data: [number, number][],
    targetGranularityMinutes: number,
  ): [number, number][] {
    if (!data || data.length < 2) {
      return data;
    }

    const timestampDiffs: number[] = [];
    for (let i = 1; i < Math.min(10, data.length); i++) {
      timestampDiffs.push(data[i][0] - data[i - 1][0]);
    }

    const modeTimestampDiff = this.mode(timestampDiffs);
    const originalGranularityMinutes = modeTimestampDiff / (60 * 1000);

    if (targetGranularityMinutes > originalGranularityMinutes) {
      return this.aggregateData(data, targetGranularityMinutes);
    }

    const targetGranularityMs = targetGranularityMinutes * 60 * 1000;
    const result: [number, number][] = [];

    for (let i = 0; i < data.length - 1; i++) {
      const currentTimestamp = data[i][0];
      const nextTimestamp = data[i + 1][0];

      result.push([currentTimestamp, data[i][1]]);

      let timestamp = currentTimestamp + targetGranularityMs;
      while (timestamp < nextTimestamp) {
        result.push([timestamp, data[i][1]]);
        timestamp += targetGranularityMs;
      }
    }

    result.push([data[data.length - 1][0], data[data.length - 1][1]]);

    return result;
  }

  private mode(arr: number[]): number {
    const frequency: Record<number, number> = {};
    let maxFreq = 0;
    let modeValue = arr[0];

    for (const num of arr) {
      frequency[num] = (frequency[num] || 0) + 1;

      if (frequency[num] > maxFreq) {
        maxFreq = frequency[num];
        modeValue = num;
      }
    }

    return modeValue;
  }

  private aggregateData(
    data: [number, number][],
    targetGranularityMinutes: number,
  ): [number, number][] {
    const targetGranularityMs = targetGranularityMinutes * 60 * 1000;
    const result: [number, number][] = [];
    const groups: Record<number, number[]> = {};

    data.forEach(([timestamp, value]) => {
      const bucketTimestamp =
        Math.floor(timestamp / targetGranularityMs) * targetGranularityMs;

      if (!groups[bucketTimestamp]) {
        groups[bucketTimestamp] = [];
      }

      groups[bucketTimestamp].push(value);
    });

    Object.entries(groups).forEach(([timestampStr, values]) => {
      const timestamp = parseInt(timestampStr);
      const avgValue =
        values.reduce((sum, val) => sum + val, 0) / values.length;
      result.push([timestamp, avgValue]);
    });

    return result.sort((a, b) => a[0] - b[0]);
  }
}
